import React, { useState, useEffect, useMemo } from 'react';
import { PropertyFilter, PropertyFilterProps } from '@amzn/awsui-components-react-v3/polaris';
import { IconButton } from '@material-ui/core';
import { HelpOutline as HelpIcon } from '@material-ui/icons';
import { useQuery } from '@tanstack/react-query';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelectorStyles } from './styles/selector.styles';
import { UrlHelper } from '../../utils/url-helper';
import { 
  QUERY_KEYS, 
  CACHE_TIMES, 
  UI_CONSTANTS,
  SELECTOR_CONSTANTS,
  PROPERTY_FILTER_I18N,
  FILTERING_PROPERTIES,
  SELECTOR_TOOLTIP,
} from './constants';
import { WebsiteViewService } from 'src/model/WebsiteViewService';
import { GenericTooltip } from './components/GenericTooltip';
import { NonCancelableEventHandler } from '@amzn/awsui-components-react-v3/polaris/internal/events';
import { 
  handleWebsiteFilterChange,
  handleWebsiteFilterLoadItems,
  parseUrlParamsToSearchQuery,
  createFilterTokensFromUrl,
  generateFilteringOptions
} from './utils/website-search';
import { LoadItemsDetail } from '@amzn/awsui-components-react-v3/polaris/property-filter/interfaces';

interface SearchQuery {
  text: string;
  queryProp: string;
}

export const WebsiteSelector = React.memo(() => {
  const [filterTokens, setFilterTokens] = useState<PropertyFilterProps.Query>({
    tokens: [],
    operation: SELECTOR_CONSTANTS.FILTER_OPERATION
  });

  const classes = useSelectorStyles();
  const history = useHistory();
  const location = useLocation();
  
  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    text: '', 
    queryProp: ''
  });

  // Listen for URL parameter changes and update search query
  useEffect(() => {
    const searchQueryFromUrl = parseUrlParamsToSearchQuery(UrlHelper.getParamValue);
    setSearchQuery(searchQueryFromUrl);
    
    const filterTokensFromUrl = createFilterTokensFromUrl(UrlHelper.getParamValue);
    setFilterTokens(filterTokensFromUrl);
  }, [location.search]);

  // React Query for website search
  const { data, isLoading, isFetching } = useQuery({
    queryKey: [QUERY_KEYS.WEBSITE_SEARCH, searchQuery.queryProp, searchQuery.text],
    queryFn: () => new WebsiteViewService().searchWebsites(
      searchQuery.text, 
      searchQuery.queryProp || SELECTOR_CONSTANTS.DEFAULT_SEARCH_PROPERTY
    ),
    enabled: !!searchQuery && searchQuery.text.length > 0,
    staleTime: UI_CONSTANTS.SEARCH_DEBOUNCE_MS,
    cacheTime: CACHE_TIMES.CACHE_TIME,
    refetchOnWindowFocus: false,
    retry: 3
  });

  const filteringOptions = useMemo(() => 
    generateFilteringOptions(data?.domainCollection, searchQuery.queryProp),
    [data, searchQuery.queryProp]
  );
  
  const handleChange: NonCancelableEventHandler<PropertyFilterProps.Query> = ({ detail }) => {
    handleWebsiteFilterChange({
      detail,
      history,
      pathname: window.location.pathname
    });
  };

  const handleLoadItems: NonCancelableEventHandler<LoadItemsDetail> = ({ detail }) => {
    handleWebsiteFilterLoadItems({
      detail,
      setSearchQuery
    });
  };

  return (
    <div className={classes.selectorContainer}>
      <div className={classes.propertyFilterContainer}>
        <PropertyFilter
          onChange={handleChange}
          expandToViewport
          query={filterTokens}
          hideOperations
          loading={isLoading || isFetching}
          virtualScroll
          tokenLimit={UI_CONSTANTS.SELECTOR.TOKEN_LIMIT}
          i18nStrings={PROPERTY_FILTER_I18N}
          filteringOptions={filteringOptions}
          filteringProperties={FILTERING_PROPERTIES}
          filteringStatusType={isLoading || isFetching ? 'loading': 'finished'}
          onLoadItems={handleLoadItems}
        />
      </div>
      <GenericTooltip
        title={SELECTOR_TOOLTIP.title}
        description={SELECTOR_TOOLTIP.description}
        placement={SELECTOR_TOOLTIP.placement}
      >
        <IconButton 
          size="medium" 
          aria-label="Help information" 
          className={classes.helpIcon}
        >
          <HelpIcon fontSize="inherit" />
        </IconButton>
      </GenericTooltip>
    </div>
  );
});

WebsiteSelector.displayName = 'WebsiteSelector';